import { AfterViewInit, computed, DestroyRef, Directive, effect, inject, Injector, input, signal, untracked } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, ControlValueAccessor, NgControl, StatusChangeEvent, TouchedChangeEvent } from "@angular/forms";
import { ReportParameterDefinition } from "..";
import { ReportWorkspaceService } from "../../report-workspace.service";



@Directive()
export abstract class ReportParameterBase<T, TOptions = unknown> implements ControlValueAccessor, AfterViewInit {
    protected readonly reportWorkspace = inject(ReportWorkspaceService);
    protected readonly injector = inject(Injector);
    protected readonly destroyRef = inject(DestroyRef);

    protected isInitialized = false;

    public readonly parameterDefinition = input.required<ReportParameterDefinition>();
    public parameterKey = computed(() => this.parameterDefinition().key);
    public parameterOptions = computed(() => this.parameterDefinition().options as TOptions);

    protected formControl: AbstractControl | null = null;

    protected isFormControlDisabled = signal<boolean>(false);
	protected isFormControlInvalid = signal<boolean>(false);
	protected isFormControlTouched = signal<boolean>(false);

    public isDisabled = computed(() => this.reportWorkspace.isReportRunning() || this.isFormControlDisabled() );
	public isInvalid = computed(() => this.isFormControlInvalid() && (this.isFormControlTouched()));

    constructor() {
        effect(() => {
            const refreshParametersRequest = this.reportWorkspace.parametersRefreshRequest();

            if (!refreshParametersRequest) { // Do nothing the first time. Value is initialized as 0.
                return;
            }

            const parameterKey = untracked(this.parameterDefinition).key;
            const value = untracked(this.reportWorkspace.parameterValues)[parameterKey];
            this.formControl?.setValue(value);
        });
    }

    ngAfterViewInit(): void {
        const control = this.injector.get(NgControl).control;

        if (!control) {
            throw new Error(`${this.parameterDefinition().key} Parameter: Form control not found`);
        }

        control.events
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(e => {
				if(e instanceof TouchedChangeEvent) {
					this.isFormControlTouched.set(e.touched);
				} 
				else if (e instanceof StatusChangeEvent) {
					this.isFormControlInvalid.set(e.status === 'INVALID');
				}
			});
        
        this.formControl = control;
        this.isFormControlInvalid.set(control.invalid);
        this.isFormControlTouched.set(control.touched);

        this.isInitialized = true;
    }

    markTouchedIfNotResetting() {
        if (!this.reportWorkspace.isResettingParameters()) {
            this.onTouched();
        }
    }

    // CVA callbacks
    protected onChange = (_: T) => {};
    protected onTouched = () => {}

    abstract writeValue(obj: T): void;

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.isFormControlDisabled.set(isDisabled);
    }

}